import React, {memo, useCallback, useState} from 'react';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import SectionHeader from '@/components/molecules/SectionHeader';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import Icon from '@/components/atoms/Icon';
import {cx} from '@/util/cx';
import styles from './HomeExpertiseShowcase.module.scss';

export interface ExpertisePanel {
    title: string;
    /** Short label shown while the panel is collapsed — a long title clips at that width. */
    shortTitle?: string;
    /** Optional paragraph shown on the expanded panel, above any pointers. */
    description?: string;
    pointers?: string[];
    src: string;
    alt: string;
}

export interface HomeExpertiseShowcaseProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    panels: ExpertisePanel[];
}

/**
 * HomeExpertiseShowcase (Organism) — the horizontal image rail where the selected
 * panel expands to reveal its capability pointers. Hover or keyboard focus selects a panel;
 * the first panel is selected on load so the section never renders "empty".
 */
const HomeExpertiseShowcase = ({eyebrow, heading, subheading, panels}: HomeExpertiseShowcaseProps) => {
    const [activeIndex, setActiveIndex] = useState(0);

    const handleActivate = useCallback((index: number) => () => setActiveIndex(index), []);

    return (
        <div className={cx('top-space mobile-top-padding bottom-spacing mobile-bottom-padding', styles.section)}>
            <Container>
                <SectionHeader
                    eyebrow={eyebrow}
                    heading={heading}
                    subheading={subheading ? parse(subheading) : subheading}
                    subheadingClassName={styles.subheading}
                />

                <div className={styles.rail}>
                    {panels.map((panel, index) => {
                        const isActive = index === activeIndex;

                        return (
                            <div
                                key={panel.title}
                                // Focusable, but not a link — keyboard users need to be able to
                                // expand a panel, and a <button> may not wrap a heading.
                                tabIndex={0}
                                aria-expanded={isActive}
                                onMouseEnter={handleActivate(index)}
                                onFocus={handleActivate(index)}
                                onClick={handleActivate(index)}
                                className={cx(
                                    styles.panel,
                                    isActive && styles.panelActive,
                                    isActive && panel.description && styles.panelActiveTall
                                )}
                            >
                                <OptimizedImage
                                    src={panel.src}
                                    alt={panel.alt}
                                    fill
                                    sizes="(max-width: 900px) 100vw, 50vw"
                                    style={{objectFit: 'cover'}}
                                    className={styles.panelImage}
                                />
                                <div className={styles.scrim} />
                                <div className={styles.panelBody}>
                                    <div className={styles.titleRow}>
                                        <h3 className={cx(styles.title, isActive && styles.titleActive, 'font-headers')}>
                                            {isActive ? panel.title : panel.shortTitle ?? panel.title}
                                        </h3>
                                        {isActive && (
                                            <div className={styles.arrow} aria-hidden="true">
                                                <Icon name="NorthEast" className={styles.arrowIcon} />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        {/* Every description is server-rendered; only the expanded panel shows its own. */}
                                        {panel.description && (
                                            <p
                                                className={cx(
                                                    styles.description,
                                                    isActive && styles.descriptionActive,
                                                    'font-regular-text',
                                                    'solution-link'
                                                )}
                                            >
                                                {parse(panel.description)}
                                            </p>
                                        )}
                                        {!!panel.pointers?.length && (
                                            <ul className={cx(styles.pointerList, isActive && styles.pointerListActive)}>
                                                {panel.pointers.map((pointer) => (
                                                    <li key={pointer} className={styles.pointerItem}>
                                                        <span aria-hidden="true" className={styles.pointerDot} />
                                                        <span className={cx(styles.pointerText, 'font-regular-text')}>{pointer}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Container>
        </div>
    );
};

export default memo(HomeExpertiseShowcase);
